import { useState } from 'react'
import { TrendingUp, Clock, Star, PanelLeftClose, Sparkles, X, Ban, Pause, RotateCcw, Settings, Loader2, UserPlus } from 'lucide-react'
import { useTopSellers } from '@/api/products'
import { useVoidReceipt } from '@/api/receipts'
import { usePOSStore } from '@/stores/posStore'
import { useUIStore } from '@/stores/uiStore'
import { useRecentProductsStore } from '@/stores/recentProductsStore'
import { useHoldCurrentReceipt, useSelectCustomer, useCloseEcomReceipt } from '@/hooks'
import { formatPrice } from '@/lib/utils'
import { ProductIcon, getCategoryIcon } from '@/lib/icons'
import { withCdnImageWidth } from '@/lib/cdnImage'
import { AddCustomerModal } from '@/components/customers/AddCustomerModal'
import { getElectronShell } from '@/lib/electronShell'
import type { Customer, Product } from '@/types'

// Resolve display fields the way the rest of the catalog UI does. The
// products/search payload puts the readable name in `display_name` /
// `description` and the brand in `brand_name` / `vendor_name`; the bare
// `name`/`brand` fields are usually empty, which is why earlier the sidebar
// only rendered prices.
function getSidebarProductFields(product: Product) {
  return {
    name: product.display_name || product.description || product.name || 'Unknown Product',
    brand: product.brand_name || product.vendor_name || product.brand || '',
    image: product.image_url || product.thumbnail_url || product.image || null,
    icon: getCategoryIcon(product.category || product.category_name || ''),
  }
}

export type QuickAccessCategory = 'trending' | 'recent' | 'favourites' | 'recommended' | null

interface LeftSidebarProps {
  onProductClick?: (product: Product) => void
}

export function LeftSidebar({ onProductClick }: LeftSidebarProps) {
  const [activeCategory, setActiveCategory] = useState<QuickAccessCategory>(null)
  const [isAddCustomerModalOpen, setIsAddCustomerModalOpen] = useState(false)
  const { selectedCustomer, addToCart, currentReceiptId, cart, isRefundMode, clearReceipt, serverTotals, loadedEcomMeta } = usePOSStore()
  const { openModal, showToast, requestProductModal } = useUIStore()
  const { holdCurrent, isPending: isHolding } = useHoldCurrentReceipt()
  const selectCustomer = useSelectCustomer()
  const voidReceipt = useVoidReceipt()
  const closeEcomReceipt = useCloseEcomReceipt()

  const handleNewCustomerSuccess = (customer: Customer) => {
    // Close the add-customer modal only on successful attach. A
    // blocked customer change (partial-payment lock) keeps the modal
    // open so the cashier sees the toast in context rather than
    // dismissing into an unchanged cart.
    selectCustomer.mutate(customer, {
      onSuccess: () => setIsAddCustomerModalOpen(false),
    })
  }

  const handleVoid = async () => {
    // "Nothing to void" only when nothing is set anywhere — including a
    // selected customer. Cashiers expect Void to be a hard reset of the
    // current transaction state (see void_flow.md: direct-clear, no
    // confirmation). A stuck customer chip with empty cart used to fall
    // into this early-return and stay on screen.
    if (cart.length === 0 && !currentReceiptId && !selectedCustomer) {
      showToast({ type: 'warning', title: 'Nothing to Void', message: 'Cart is empty.' })
      return
    }

    // Refund-in-progress: void the BE refund receipt too. Without this,
    // the persisted refund receipt (created by copy-with-refund) stays
    // at status=New with the cashier's per-item voids intact. The
    // idempotency check in copy_with_refund's BE
    // (status.in_([New, Hold]) && source_number==X && is_return==true)
    // would then match the modified receipt on the next refund
    // attempt and return it instead of building a fresh one -- the
    // cashier sees the same partial state they were trying to throw
    // away. Setting status=Deleted via the void endpoint takes the
    // refund out of that idempotency window so the next refund creates
    // R2 from a fresh src.items snapshot.
    //
    // refundReceiptId guard: enterRefundMode always sets
    // currentReceiptId from copy-with-refund's response, so a refund-
    // mode flag without a receipt id is an inconsistent state. Detach
    // locally in that branch rather than firing a void with a null id.
    if (isRefundMode) {
      const refundReceiptId = currentReceiptId
      if (refundReceiptId == null) {
        clearReceipt()
        showToast({ type: 'success', title: 'Refund Cancelled', message: 'Refund has been cancelled.' })
        return
      }
      try {
        await voidReceipt.mutateAsync({ receiptId: refundReceiptId })
      } catch (err: unknown) {
        // already_deleted (409) is the benign reverse-direction: the
        // receipt is already gone from the idempotency window, so the
        // next refund will still create a fresh one. Treat the same as
        // success.
        const axiosErr = err as { response?: { status?: number; data?: { code?: string; error?: string } } }
        const code = axiosErr?.response?.data?.code
        const status = axiosErr?.response?.status
        const errMsg = (axiosErr?.response?.data?.error || (err instanceof Error ? err.message : '')).toLowerCase()
        const isAlreadyGone =
          code === 'already_deleted'
          || status === 404
          || errMsg.includes('already deleted')
        if (!isAlreadyGone) {
          // Surface the failure so the cashier knows the BE refund is
          // still live; clearing locally would leave a stuck refund
          // matched on the next attempt.
          const msg = axiosErr?.response?.data?.error || (err instanceof Error ? err.message : 'Void failed.')
          console.warn('[refund] void failed', err)
          showToast({ type: 'error', title: 'Cancel Failed', message: msg })
          return
        }
      }
      clearReceipt()
      showToast({ type: 'success', title: 'Refund Cancelled', message: 'Refund has been cancelled.' })
      return
    }

    // Loaded ecom/delivery order: CLOSE (revert to its pre-load status +
    // clear), never cancel — the clerk may just be viewing it with no
    // customer present. Real cancellation stays on the OrderDetailModal
    // "Cancel Order" button (/cancel-order, which emails the customer).
    if (loadedEcomMeta) {
      try {
        await closeEcomReceipt.close()
        showToast({ type: 'success', title: 'Order Closed', message: 'Order returned to the list. It was not cancelled.' })
      } catch (err) {
        showToast({
          type: 'error',
          title: 'Could not close order',
          message: err instanceof Error ? err.message : undefined,
        })
      }
      return
    }

    if (currentReceiptId) {
      // Pre-flight: when we already know the receipt is fully paid
      // (serverTotals.amountToPay == 0), the BE will refuse the void
      // ("Cannot void a receipt with payments"). Skip the round-trip
      // and just detach locally — the cashier is using Void as an
      // escape hatch from a stuck post-payment UI state (e.g. modal
      // re-mounted after PIN re-auth on the 'complete' step). The
      // backend receipt stays intact and finished.
      const amountToPay = serverTotals?.amountToPay
      if (amountToPay !== undefined && amountToPay !== null && Math.abs(amountToPay) <= 0.005) {
        clearReceipt()
        // Match the BE-failure branch wording below — same root cause
        // (paid receipt, void requested) should read identically to the
        // cashier whether we caught it pre-flight or via the BE refusal.
        showToast({
          type: 'warning',
          title: 'Receipt Already Closed',
          message: "Paid receipts can't be voided. Cart cleared — use Refund/Return for paid orders.",
        })
        return
      }
      try {
        await voidReceipt.mutateAsync({ receiptId: currentReceiptId })
      } catch (err: unknown) {
        const axiosErr = err as { response?: { status?: number; data?: { code?: string; error?: string } } }
        const code = axiosErr?.response?.data?.code
        const errMsg = (axiosErr?.response?.data?.error || (err instanceof Error ? err.message : '')).toLowerCase()

        // Terminal — receipt is already in a state the backend won't
        // let us mutate (paid, locked, voided, deleted, mid-payment).
        // The previous code returned without clearReceipt(), which left
        // the cart and selectedCustomer wired to a receipt the cashier
        // could no longer act on — every subsequent Void retried and
        // 500'd. clearReceipt() is local-only, so it's safe to run
        // against a paid receipt; the backend record stays untouched.
        //
        // We treat HTTP 409 (Conflict — proper "cannot mutate this
        // resource" signal) AND HTTP 500 as terminal. The 500 inclusion
        // matches the v2 backend's actual behavior: paid-receipt voids
        // currently throw a generic 500 instead of a structured 409
        // (see the Network panel — `<html>500: Internal Server
        // Error</html>` with no code/error body). Without the 500
        // catch, the cart stays stuck because there's no detectable
        // hint in the response body.
        //
        // The 'with payments' / 'complete the transaction' / 'cancel
        // any payments' phrases catch the v2 `v2_operation_failed`
        // wrapper response when the BE refuses to void a receipt that
        // has tendered payments — same terminal class as
        // cannot_cancel_paid, just less structured.
        const status = axiosErr?.response?.status
        const isTerminal =
          status === 409
          || status === 500
          || code === 'cannot_cancel_paid'
          || code === 'already_terminal'
          || code === 'payment_in_progress'
          || errMsg.includes('is locked')
          || errMsg.includes('cannot be modified')
          || errMsg.includes('already paid')
          || errMsg.includes('with payments')
          || errMsg.includes('cancel any payments')

        if (isTerminal) {
          clearReceipt()
          showToast({
            type: 'warning',
            title: 'Receipt Already Closed',
            message:
              code === 'cannot_cancel_paid' || errMsg.includes('already paid') || errMsg.includes('with payments')
                ? "Paid receipts can't be voided. Cart cleared — use Refund/Return for paid orders."
                : 'Receipt is closed on the backend. Cart cleared so you can start fresh.',
          })
          return
        }

        const msg = axiosErr?.response?.data?.error || (err instanceof Error ? err.message : 'Void failed.')
        showToast({ type: 'error', title: 'Void Failed', message: msg })
        return
      }
    }
    clearReceipt()
    showToast({ type: 'success', title: 'Receipt Voided', message: 'Transaction has been cancelled.' })
  }

  // Fetch top sellers
  const { data: topSellers = [] } = useTopSellers()

  // Recent products are session-scoped (not per-customer) — every modal
  // open in /pos pushes through useRecentProductsStore.
  const recentProducts = useRecentProductsStore((s) => s.recentProducts)

  const isGuestCustomer = !selectedCustomer
  const isExpanded = activeCategory !== null

  const handleCategoryClick = (category: QuickAccessCategory) => {
    setActiveCategory(activeCategory === category ? null : category)
  }

  const handleClose = () => {
    setActiveCategory(null)
  }

  const handleProductClick = (product: Product) => {
    // Sidebar panels are browse/inspect surfaces — every click opens
    // the modal so the cashier can pick a batch / confirm price before
    // committing to the cart. `trackRecent: false` for Recent keeps
    // the panel's ordering stable while the cashier browses it.
    if (activeCategory) {
      requestProductModal(product, {
        trackRecent: activeCategory !== 'recent',
      })
      return
    }
    if (onProductClick) {
      onProductClick(product)
    } else {
      addToCart(product, 1)
    }
  }

  const getActiveProducts = (): Product[] => {
    switch (activeCategory) {
      case 'trending':
        return topSellers
      case 'recent':
        return recentProducts
      case 'favourites':
        return [] // TODO: Implement favorites
      case 'recommended':
        return [] // TODO: Implement recommendations
      default:
        return []
    }
  }

  const getCategoryTitle = (): string => {
    switch (activeCategory) {
      case 'trending':
        return 'Top Sellers'
      case 'recent':
        return 'Recent Products'
      case 'favourites':
        return 'Favourites'
      case 'recommended':
        return 'Recommended'
      default:
        return ''
    }
  }

  const activeProducts = getActiveProducts()

  return (
    <div className="flex h-full bg-white border-r border-gray-200 overflow-visible relative z-30 flex-shrink-0">
      {/* Icon buttons column */}
      <div className="w-16 flex flex-col items-center py-4 gap-2 shrink-0">
        <button
          onClick={handleClose}
          disabled={!isExpanded}
          className={`p-2 rounded-lg transition-colors ${
            isExpanded
              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer'
              : 'text-gray-300 cursor-not-allowed'
          }`}
          title={isExpanded ? 'Collapse' : 'No panel open'}
        >
          <PanelLeftClose size={20} />
        </button>

        <button
          onClick={() => handleCategoryClick('trending')}
          className={`p-2 rounded-lg cursor-pointer transition-colors ${
            activeCategory === 'trending'
              ? 'bg-green-100 text-green-600'
              : 'text-gray-500 hover:bg-gray-100'
          }`}
          title="Top Sellers"
        >
          <TrendingUp size={20} />
        </button>

        <button
          onClick={() => handleCategoryClick('recent')}
          className={`p-2 rounded-lg cursor-pointer transition-colors ${
            activeCategory === 'recent'
              ? 'bg-green-100 text-green-600'
              : 'text-gray-500 hover:bg-gray-100'
          }`}
          title="Recent Products"
        >
          <Clock size={20} />
        </button>

        <button
          onClick={() => !isGuestCustomer && handleCategoryClick('favourites')}
          disabled={isGuestCustomer}
          className={`p-2 rounded-lg transition-colors ${
            isGuestCustomer
              ? 'text-gray-300 cursor-not-allowed'
              : activeCategory === 'favourites'
                ? 'bg-green-100 text-green-600 cursor-pointer'
                : 'text-gray-500 hover:bg-gray-100 cursor-pointer'
          }`}
          title={isGuestCustomer ? 'Select a customer to view favourites' : 'Favourites'}
        >
          <Star size={20} />
        </button>

        <button
          onClick={() => !isGuestCustomer && handleCategoryClick('recommended')}
          disabled={isGuestCustomer}
          className={`p-2 rounded-lg transition-colors ${
            isGuestCustomer
              ? 'text-gray-300 cursor-not-allowed'
              : activeCategory === 'recommended'
                ? 'bg-green-100 text-green-600 cursor-pointer'
                : 'text-gray-500 hover:bg-gray-100 cursor-pointer'
          }`}
          title={isGuestCustomer ? 'Select a customer to view recommendations' : 'Recommended'}
        >
          <Sparkles size={20} />
        </button>
        
        {/* Divider */}
        <hr className="w-10 border-gray-200 my-2" />
        
        {/* Quick Actions with labels */}
        <button
          onClick={handleVoid}
          className="flex flex-col items-center gap-1 p-1 rounded-lg hover:bg-red-50 transition-colors cursor-pointer"
          title="Void"
        >
          <div className="w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center">
            <Ban size={16} className="text-white" />
          </div>
          <span className="text-[9px] text-gray-500 font-medium">Void</span>
        </button>

        <button
          onClick={() => holdCurrent()}
          disabled={isHolding}
          className="flex flex-col items-center gap-1 p-1 rounded-lg hover:bg-orange-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer"
          title="Hold"
        >
          <div className="w-8 h-8 rounded-lg bg-orange-400 flex items-center justify-center">
            {isHolding ? <Loader2 size={16} className="text-white animate-spin" /> : <Pause size={16} className="text-white" />}
          </div>
          <span className="text-[9px] text-gray-500 font-medium">Hold</span>
        </button>

        <button
          onClick={() => openModal('held-receipts')}
          className="flex flex-col items-center gap-1 p-1 rounded-lg hover:bg-green-50 transition-colors cursor-pointer"
          title="Recall"
        >
          <div className="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center">
            <RotateCcw size={16} className="text-white" />
          </div>
          <span className="text-[9px] text-gray-500 font-medium">Recall</span>
        </button>
        
        <button
          onClick={() => setIsAddCustomerModalOpen(true)}
          className="flex flex-col items-center gap-1 p-1 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer"
          title="New Customer"
        >
          <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
            <UserPlus size={16} className="text-white" />
          </div>
          <span className="text-[9px] text-gray-500 font-medium">Customer</span>
        </button>

        {/* Spacer to push settings to bottom */}
        <div className="flex-1" />

        {/* Shell settings — only rendered when running inside the
            waio-windows-v2 Electron wrapper, which exposes window.waio
            via a preload bridge. Invisible in normal browsers. */}
        {getElectronShell() && (
          <button
            onClick={() => { void getElectronShell()?.openSettings() }}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            title="Shell settings"
          >
            <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center">
              <Settings size={16} className="text-gray-600" />
            </div>
          </button>
        )}
      </div>

      {/* Sliding product list panel */}
      <div
        className={`flex flex-col overflow-hidden border-l transition-[width] duration-300 ease-out ${
          isExpanded ? 'w-56 border-gray-200' : 'w-0 border-transparent'
        }`}
      >
        <div className="w-56 h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-gray-200">
            <span className="font-medium text-gray-700">{getCategoryTitle()}</span>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-gray-100 rounded cursor-pointer text-gray-500 hover:text-gray-700"
            >
              <X size={18} />
            </button>
          </div>

          {/* Product list */}
          <div className="flex-1 overflow-y-auto p-3">
            {activeProducts.length > 0 ? (
              <div className="space-y-2">
                {activeProducts.map((product) => {
                  const p = getSidebarProductFields(product)
                  return (
                    <button
                      key={product.id}
                      onClick={() => handleProductClick(product)}
                      className="w-full bg-gray-50 hover:bg-gray-100 rounded-lg p-2 text-left transition-colors cursor-pointer"
                      title={p.name}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {p.image ? (
                            <img
                              src={withCdnImageWidth(p.image, 100)}
                              alt={p.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none'
                              }}
                            />
                          ) : (
                            <ProductIcon name={p.icon} size={16} className="text-green-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {p.name}
                          </p>
                          {p.brand && (
                            <p className="text-xs text-gray-500 truncate">{p.brand}</p>
                          )}
                        </div>
                        <span className="text-sm font-semibold text-green-600 flex-shrink-0">
                          ${formatPrice(product.price)}
                        </span>
                      </div>
                    </button>
                  )
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 text-sm">
                {activeCategory === 'recommended' ? (
                  <>
                    <Sparkles size={32} className="mb-2 opacity-50" />
                    <p>No recommendations</p>
                    <p className="text-xs mt-1 text-center px-2">
                      Add tags to customer profile for personalized suggestions
                    </p>
                  </>
                ) : (
                  <>
                    <Star size={32} className="mb-2 opacity-50" />
                    <p>No items yet</p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {isAddCustomerModalOpen && (
        <AddCustomerModal
          onClose={() => setIsAddCustomerModalOpen(false)}
          onSuccess={handleNewCustomerSuccess}
        />
      )}
    </div>
  )
}
